import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Modal } from "reactstrap";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CreditCardIcon from "mdi-react/CreditCardIcon";
import LinearLoading from "../../../shared/components/LinearLoading";
import BellPlusIcon from "mdi-react/BellPlusOutlineIcon";
import { _axiosCall } from "../../../shared/helpers/apicalls";
import { buildExpiredListingsQuery } from "../../../shared/helpers/expiredListingsQuery";
import { getCohortQuery, expiredListingsFL } from "../../../shared/helpers/utils";
import { setUserProfile } from "../../../redux/actions/authActions";
import AutoRechargeSettings from "../../../shared/components/premium_credits/AutoRechargeSettings";
import StripeCardSetupModal from "../../../shared/components/payment/stripe/StripeCardSetupModal";
import { premiumCreditPacks } from "../../../shared/components/premium_credits/PremiumCreditsPurchaseModal";
import { _formatNumber } from "../../../shared/helpers/utils";
import {
	buildFilterChips,
	buildGeoChips,
	_titleCase,
	_prettyValue
} from "./alertFilterFormatters";

function _getUserPremiumCredits(user) {
	if (!user) return 0;
	if (typeof user.premiumCredits === "number") return user.premiumCredits;
	if (user.profile && typeof user.profile.premiumCredits === "number") return user.profile.premiumCredits;
	return 0;
}

// Same estimate logic the bulk skip-trace modal uses — Premium Credit cost is
// 1 per record at the upper bound; cache hits and no-match results bring it down.
function _estimateCreditRange(recordCount) {
	const est = recordCount;
	return {
		low: Math.round(est * 0.75),
		est: est,
		high: Math.round(est * 1.25)
	};
}

const apiUrl = process.env.API_ROOT;

const STRIPE_PREMIUM_CREDITS_PRODUCT_ID =
	process.env.NODE_ENV === "development" || process.env.NODE_ENV === "local"
		? "prod_TzWUHN69NLB7WH"
		: "prod_TzWWtocat4f6kG";

const STRIPE_TAX_RATE_ID =
	process.env.NODE_ENV === "development" || process.env.NODE_ENV === "local"
		? "txr_1HPuUwG44QC64PsRi91xssbx"
		: "txr_1HCVNJG44QC64PsRdrtshCba";

function _getThemeClass() {
	if (typeof document === "undefined") return "";
	if (document.querySelector(".role-bhs-agent")) return "role-bhs-agent";
	if (document.querySelector(".role-elliman-agent")) return "role-elliman-agent";
	return "";
}

const OFF_MARKET_TYPES = [
	{
		key: "expired",
		label: "Expired",
		hint: "Listing's contract term ran out — broker relationship is open."
	},
	{
		key: "pom",
		label: "Permanently off-market",
		hint: "Owner withdrew the listing without re-listing."
	},
	{
		key: "tom",
		label: "Temporarily off-market",
		hint: "Often re-listed soon. Higher volume, more noise."
	}
];

const DEFAULT_TYPES = ["expired", "pom"];

const SUMMARY_KEYS_SKIP = new Set([
	"off_market_type",
	"min_off_market_date",
	"max_off_market_date"
]);

class SaveAlertModal extends PureComponent {
	state = {
		name: "",
		offMarketTypes: DEFAULT_TYPES.slice(),
		maxResults: 10,
		// String mirror of `maxResults` for the controlled input. Lets the field
		// be transiently empty/partial while typing (e.g. clearing "10" to type
		// "3") without snapping back to a default — which would make low values
		// unreachable. Committed back to `maxResults` on blur/submit.
		maxResultsInput: "10",
		enableSkipTrace: false,
		hasCard: null,         // null = unknown / checking, true = has card, false = needs card
		checkingCard: false,
		paymentMethod: null,
		setupClientSecret: null,
		showPaymentModal: false,
		submitting: false,
		error: null,
		success: false,
		probing: false,
		probedTotal30d: null,
		probedActiveDays: null,
		probedDailyAvg: null
	};

	componentDidUpdate(prevProps, prevState) {
		if (this.props.open && !prevProps.open) {
			const sub = this.props.subscription;
			const skipOn = !!(sub && sub.skipTraceEnabled);
			const initialMax = sub && sub.maxResults ? sub.maxResults : 10;
			this.setState({
				name: sub ? (sub.name || "") : this._suggestName(),
				offMarketTypes: sub && Array.isArray(sub.offMarketTypes) && sub.offMarketTypes.length > 0
					? sub.offMarketTypes.slice()
					: DEFAULT_TYPES.slice(),
				maxResults: initialMax,
				maxResultsInput: String(initialMax),
				enableSkipTrace: skipOn,
				hasCard: null,
				checkingCard: false,
				paymentMethod: null,
				setupClientSecret: null,
				showPaymentModal: false,
				submitting: false,
				error: null,
				success: false,
				probing: false,
				probedTotal30d: null,
				probedActiveDays: null,
				probedDailyAvg: null
			});
			this._checkCard();
			if (skipOn) this._probeRunSize();
		}
		// Re-probe when the toggle flips on, or when off-market types change while on
		if (this.props.open) {
			const justEnabled = this.state.enableSkipTrace && !prevState.enableSkipTrace;
			const typesChanged =
				this.state.enableSkipTrace &&
				prevState.offMarketTypes &&
				prevState.offMarketTypes.join(",") !== this.state.offMarketTypes.join(",");
			if (justEnabled || typesChanged) this._probeRunSize();
		}
	}

	_isEditMode = () => !!(this.props.subscription && this.props.subscription._id);

	// In edit mode the search-page props (subFilters, searchTags) aren't passed
	// from the search context — read them off the saved subscription instead.
	_effectiveSubFilters = () => {
		if (this._isEditMode()) return (this.props.subscription && this.props.subscription.filters) || {};
		return this.props.subFilters || {};
	};

	_effectiveSearchTags = () => {
		if (this._isEditMode()) return (this.props.subscription && this.props.subscription.searchTags) || [];
		return this.props.searchTags || [];
	};

	// --- run-size probe -------------------------------------------------------
	// Hit Solr with the same filters + a 30-day off-market window so we can
	// show a realistic "X listings/day on average" instead of assuming the
	// alert always hits its cap.

	_probeRunSize = async () => {
		const { user } = this.props;
		const { offMarketTypes } = this.state;
		if (!user || !user.id) return;
		if (!offMarketTypes || offMarketTypes.length === 0) return;

		const subFiltersForProbe = Object.assign({}, this._effectiveSubFilters());
		SUMMARY_KEYS_SKIP.forEach(k => delete subFiltersForProbe[k]);

		const built = buildExpiredListingsQuery({
			searchMode: "off_market",
			subFilters: subFiltersForProbe,
			buildingStages: this.props.buildingStages || ["all"],
			searchText: this.props.searchText || "",
			searchTags: this._effectiveSearchTags(),
			orderBy: "",
			order: "desc",
			defaultBuildingQ: getCohortQuery(user, true),
			fl: ["id"]
		});

		const TYPE_FIELDS = {
			expired: "last_listed_sale_expired_date_formatted",
			pom: "last_listed_sale_pom_date_formatted",
			tom: "last_listed_sale_tom_date_formatted"
		};
		const dateClause = offMarketTypes
			.map(t => TYPE_FIELDS[t] && TYPE_FIELDS[t] + ":[NOW-30DAYS TO NOW]")
			.filter(Boolean)
			.join(" OR ");
		if (!dateClause) return;
		const probeQ = "(" + (built.q || "*:*") + ") AND (" + dateClause + ")";

		// Sub-facet per type, day-bucketed. Lets us compute "active days" =
		// days where at least one type fired — the right denominator for cost
		// per email (the alert only sends on days with matches).
		const facets = {};
		offMarketTypes.forEach(t => {
			if (!TYPE_FIELDS[t]) return;
			facets["by_day_" + t] = {
				type: "range",
				field: TYPE_FIELDS[t],
				start: "NOW-30DAYS/DAY",
				end: "NOW/DAY",
				gap: "+1DAY"
			};
		});

		this.setState({ probing: true });
		try {
			const res = await _axiosCall({
				url: `${apiUrl}/query/we3-properties?cache=300`,
				method: "post",
				query: {
					q: probeQ,
					fq: built.fq,
					rows: 0,
					wt: "json",
					"json.facet": facets
				}
			});
			const total = (res && res.numFound) || 0;
			// Union counts across types, day by day, then count days that had
			// at least one match.
			const dayTotals = [];
			Object.keys(facets).forEach(name => {
				const f = res && res.facets && res.facets[name];
				const buckets = (f && f.buckets) || [];
				buckets.forEach((b, i) => {
					dayTotals[i] = (dayTotals[i] || 0) + (b.count || 0);
				});
			});
			const activeDays = dayTotals.filter(c => c > 0).length;
			this.setState({
				probing: false,
				probedTotal30d: total,
				probedActiveDays: activeDays,
				probedDailyAvg: total / 30
			});
		} catch (e) {
			this.setState({ probing: false });
		}
	};

	// --- payment / card-on-file -----------------------------------------------
	// Mirrors BulkSkipTraceModal so the user experience is identical when
	// opting into skip-trace from anywhere in the app.

	_checkCard = () => {
		const { user } = this.props;
		if (!user || !user.customerId) {
			this.setState({ hasCard: false, checkingCard: false });
			return;
		}
		this.setState({ checkingCard: true });
		_axiosCall({
			url: apiUrl + "/buyerslist/stripe/get-payment-methods",
			method: "post",
			query: { customer_id: user.customerId }
		})
			.then(res => {
				if (res && res.success && res.methods && res.methods.length > 0) {
					this.setState({ hasCard: true, paymentMethod: res.methods[0], checkingCard: false });
				} else {
					this.setState({ hasCard: false, checkingCard: false });
				}
			})
			.catch(() => this.setState({ hasCard: false, checkingCard: false }));
	};

	_createSetupIntent = () => {
		const { user } = this.props;
		const doSetupIntent = customerId => {
			_axiosCall({
				url: apiUrl + "/buyerslist/stripe/create-setup-intent",
				method: "post",
				query: { customer_id: customerId }
			}).then(res => {
				if (res && res.client_secret) {
					this.setState({ setupClientSecret: res.client_secret, showPaymentModal: true });
				}
			});
		};
		if (user.customerId) {
			doSetupIntent(user.customerId);
		} else {
			_axiosCall({
				url: apiUrl + "/buyerslist/stripe/create-basic-customer",
				method: "post",
				query: {
					userId: user.id || user.sub,
					name: (user.firstName && user.lastName) ? user.firstName + " " + user.lastName : "",
					email: user.email
				}
			}).then(res => {
				if (res && res.customerId) {
					this.props.setUserProfile(Object.assign({}, user, { customerId: res.customerId }));
					doSetupIntent(res.customerId);
				}
			});
		}
	};

	_suggestName = () => {
		const sf = this._effectiveSubFilters();
		const tags = this._effectiveSearchTags();
		const parts = [];

		// Geography (from search bar tags) leads the name — it's the most
		// distinguishing signal for users with multiple alerts.
		const geoBits = tags
			.map(t => {
				if (!t || !t.type) return null;
				if (t.type === "borough") return _titleCase(t.data || "");
				if (t.type === "neighborhood") return _titleCase((t.data && t.data.hood) || "");
				if (t.type === "zip") return String(t.data);
				if (t.type === "building") {
					const b = t.data || {};
					return b.name || b.display || null;
				}
				return null;
			})
			.filter(Boolean);
		if (geoBits.length > 0) parts.push(geoBits.join(" + "));

		if (sf.ownership_type) {
			const v = Array.isArray(sf.ownership_type) ? sf.ownership_type : [sf.ownership_type];
			parts.push(v.map(x => _prettyValue("ownership_type", x)).join("/"));
		}
		if (sf.bedrooms) {
			const v = Array.isArray(sf.bedrooms) ? sf.bedrooms : [sf.bedrooms];
			parts.push(v.map(x => _prettyValue("bedrooms", x)).join("/"));
		}

		const label = parts.length > 0 ? parts.join(" · ") : "All NYC";
		return `Off-market alert: ${label}`;
	};

	_toggleType = key => {
		const next = this.state.offMarketTypes.includes(key)
			? this.state.offMarketTypes.filter(k => k !== key)
			: [...this.state.offMarketTypes, key];
		this.setState({ offMarketTypes: next });
	};

	_filterSummary = () => {
		// Geography first, then filter chips — both via shared formatters.
		return [
			...buildGeoChips(this._effectiveSearchTags()),
			...buildFilterChips(this._effectiveSubFilters())
		];
	};

	_handleSubmit = async () => {
		const { user } = this.props;
		const { name, offMarketTypes, maxResults, enableSkipTrace, hasCard } = this.state;

		if (!name || name.trim().length === 0) {
			this.setState({ error: "Please give your alert a name." });
			return;
		}
		if (offMarketTypes.length === 0) {
			this.setState({ error: "Pick at least one off-market type." });
			return;
		}
		if (!user || !user.id) {
			this.setState({ error: "We couldn't identify your account. Please re-login." });
			return;
		}
		// Block save if skip-trace is on but the user can't pay for it.
		if (enableSkipTrace) {
			const ar = (user && user.autoRecharge) || {};
			if (!hasCard) {
				this.setState({ error: "Add a credit card to enable skip-trace." });
				return;
			}
			if (!ar.enabled || !ar.pack_id) {
				this.setState({ error: "Enable auto-recharge to keep skip-trace running." });
				return;
			}
		}

		this.setState({ submitting: true, error: null });

		// Build the auto-recharge payload in the same shape as BulkSkipTraceModal
		// so the backend can reuse the existing recharge logic.
		const ar = (user && user.autoRecharge) || {};
		let autoRechargePayload = { enabled: false };
		if (enableSkipTrace && ar.enabled && ar.pack_id) {
			const pack = premiumCreditPacks.find(p => p.id === ar.pack_id);
			if (pack) {
				autoRechargePayload = {
					enabled: true,
					pack_id: ar.pack_id,
					threshold: ar.threshold || 10,
					creditConfig: {
						id: pack.id,
						label: pack.label,
						credits: pack.credits,
						pricePerCredit: pack.pricePerCredit,
						stripeProductId: STRIPE_PREMIUM_CREDITS_PRODUCT_ID,
						taxCode: STRIPE_TAX_RATE_ID
					}
				};
			}
		}

		const editMode = this._isEditMode();
		let body;

		if (editMode) {
			// Edit mode keeps the saved query untouched — only the surface-level
			// settings change. Filter changes still require recreating the alert
			// from the search page.
			body = {
				name: name.trim(),
				offMarketTypes,
				maxResults,
				skipTraceEnabled: !!enableSkipTrace,
				autoRecharge: autoRechargePayload
			};
		} else {
			const subFiltersForStorage = Object.assign({}, this.props.subFilters || {});
			SUMMARY_KEYS_SKIP.forEach(k => delete subFiltersForStorage[k]);

			const built = buildExpiredListingsQuery({
				searchMode: "off_market",
				subFilters: subFiltersForStorage,
				buildingStages: this.props.buildingStages || ["all"],
				searchText: this.props.searchText || "",
				searchTags: this.props.searchTags || [],
				orderBy: this.props.orderBy || "",
				order: this.props.order || "desc",
				defaultBuildingQ: getCohortQuery(user, true),
				fl: expiredListingsFL
			});

			body = {
				name: name.trim(),
				filters: subFiltersForStorage,
				searchTags: this.props.searchTags || [],
				offMarketTypes,
				maxResults,
				storedQ: built.q,
				storedFq: built.fq,
				storedSort: built.sort,
				storedFl: built.fl,
				skipTraceEnabled: !!enableSkipTrace,
				autoRecharge: autoRechargePayload
			};
		}

		try {
			const url = editMode
				? `${apiUrl}/expired-listing-alerts/${encodeURIComponent(user.id)}/${this.props.subscription._id}`
				: `${apiUrl}/expired-listing-alerts/${encodeURIComponent(user.id)}`;
			const res = await _axiosCall({
				url: url,
				method: editMode ? "put" : "post",
				query: body,
				headers: { "Content-Type": "application/json" }
			});
			if (res && res.error) {
				this.setState({ submitting: false, error: res.error });
				return;
			}
			this.setState({ submitting: false, success: true });
			if (this.props.onSaved) this.props.onSaved(res);
		} catch (e) {
			this.setState({ submitting: false, error: "Save failed. Please try again." });
		}
	};

	_renderSkipTraceSection() {
		const { user } = this.props;
		const { enableSkipTrace, hasCard, checkingCard, maxResults, probing, probedDailyAvg, probedTotal30d, probedActiveDays } = this.state;
		const ar = (user && user.autoRecharge) || {};
		const optimalPack = premiumCreditPacks && premiumCreditPacks[0];
		const currentCredits = _getUserPremiumCredits(user);

		// Monthly cost band:
		//   LOW  = the actual measured monthly cost, discounted ~25% for cache hits
		//          (capped per-day at maxResults — same way the run will execute)
		//   HIGH = the cap × active days (worst case if every active day hit the cap).
		// This makes the user's per-day cap a visible lever: raising it widens HIGH,
		// lowering it tightens HIGH, regardless of whether the cap actually binds today.
		const activeDays = (probedActiveDays && probedActiveDays > 0) ? probedActiveDays : null;
		let monthlyLikely; // capped, measured
		if (probedTotal30d !== null && probedTotal30d !== undefined && activeDays) {
			const avgPerActiveDay = probedTotal30d / activeDays;
			monthlyLikely = Math.min(avgPerActiveDay, maxResults) * activeDays;
		} else if (probedTotal30d !== null && probedTotal30d !== undefined) {
			monthlyLikely = probedTotal30d;
		} else {
			// Pre-probe fallback: assume one match per day (rough placeholder).
			monthlyLikely = 30;
		}
		const monthlyMaxIfCapped = activeDays
			? activeDays * maxResults
			: monthlyLikely; // no probe yet → can't show a cap line
		const lowCredits = Math.max(0, Math.round(monthlyLikely * 0.75));
		const highCredits = Math.max(0, Math.round(monthlyMaxIfCapped));
		const range = { low: lowCredits, est: Math.max(0, Math.round(monthlyLikely)), high: Math.max(highCredits, lowCredits) };

		return (
			<div className={"el-alert-modal__skiptrace" + (enableSkipTrace ? " el-alert-modal__skiptrace--active" : "")}>
				<div className="el-alert-modal__skiptrace__head">
					<div className="el-alert-modal__skiptrace__title">Skip-trace owner contacts</div>
					<FormControlLabel
						className="el-alert-modal__skiptrace__toggle"
						control={
							<Switch
								checked={enableSkipTrace}
								onChange={e => this.setState({ enableSkipTrace: e.target.checked })}
								color="primary"
							/>
						}
						label={
							<span className="el-alert-modal__skiptrace__label">
								Include owner phone, email, and mailing address with each daily email
							</span>
						}
					/>
					<div className="el-alert-modal__skiptrace__hint">
						Uses Premium Credits. Up to {maxResults} per email. Previously-traced owners are free.
					</div>
				</div>

				{enableSkipTrace && (() => {
					// Pick a per-credit price: prefer the user's chosen auto-recharge
					// pack so the dollar estimate matches what they'll be billed.
					const userPack = ar.pack_id
						? premiumCreditPacks.find(p => p.id === ar.pack_id)
						: null;
					const pack = userPack || optimalPack;
					const pricePerCredit = pack ? pack.pricePerCredit : 0;
					const dollarLow = (range.low * pricePerCredit).toFixed(2);
					const dollarHigh = (range.high * pricePerCredit).toFixed(2);
					const hasProbe = probedTotal30d !== null && probedTotal30d !== undefined;
					const emailsPerMonth = probedActiveDays || 0;
					const avgPerEmail = hasProbe && emailsPerMonth > 0
						? Math.min(probedTotal30d / emailsPerMonth, maxResults)
						: 0;
					// Expected leads = listings included in your emails (capped, measured).
					// Show as a primary stat alongside cost — what users came here to know.
					const leadsLow = range.est;
					const leadsHigh = range.high;
					const leadsLabel = !hasProbe
						? "—"
						: leadsLow === leadsHigh
							? `~${_formatNumber(leadsLow)}`
							: `${_formatNumber(leadsLow)}–${_formatNumber(leadsHigh)}`;
					return (
						<div className="el-alert-modal__skiptrace__cost">
							{probing && (
								<div className="el-alert-modal__skiptrace__cost__row">
									<span className="el-alert-modal__skiptrace__cost__label">
										Calculating estimate…
									</span>
								</div>
							)}
							{!probing && (
								<Fragment>
									<div className="el-alert-modal__skiptrace__cost__row">
										<span className="el-alert-modal__skiptrace__cost__label">Expected leads per month</span>
										<span className="el-alert-modal__skiptrace__cost__value">{leadsLabel}</span>
									</div>
									{hasProbe && (
										<div className="el-alert-modal__skiptrace__cost__sub-row">
											<span className="el-alert-modal__skiptrace__cost__sub-label">
												{emailsPerMonth === 0
													? "No recent matches in your filters"
													: `${emailsPerMonth} email${emailsPerMonth === 1 ? "" : "s"}/month · ${avgPerEmail < 1 ? "<1" : Math.round(avgPerEmail)} listing${Math.round(avgPerEmail) === 1 ? "" : "s"} each`}
											</span>
										</div>
									)}
									<div className="el-alert-modal__skiptrace__cost__row">
										<span className="el-alert-modal__skiptrace__cost__label">Estimated cost per month</span>
										<span className="el-alert-modal__skiptrace__cost__value">{`$${dollarLow}–$${dollarHigh}`}</span>
									</div>
									<div className="el-alert-modal__skiptrace__cost__sub-row">
										<span className="el-alert-modal__skiptrace__cost__sub-label">
											{_formatNumber(range.low)}–{_formatNumber(range.high)} Premium Credits/month
										</span>
										<span className="el-alert-modal__skiptrace__cost__sub-label">
											@ ${pricePerCredit.toFixed(2)}/credit
										</span>
									</div>
								</Fragment>
							)}
							<div className="el-alert-modal__skiptrace__cost__row el-alert-modal__skiptrace__cost__row--secondary">
								<span className="el-alert-modal__skiptrace__cost__label">Your balance</span>
								<span className="el-alert-modal__skiptrace__cost__value">
									{_formatNumber(currentCredits)} Premium Credits
								</span>
							</div>
							<div className="el-alert-modal__skiptrace__cost__sub">
								{hasProbe
									? `Based on ${_formatNumber(probedTotal30d)} matches over ${emailsPerMonth} active day${emailsPerMonth === 1 ? "" : "s"} in the past 30. Range reflects cache savings and owners with no match.`
									: "Range reflects cache savings and owners with no match. Auto-recharge keeps your balance topped up."}
							</div>
						</div>
					);
				})()}

				{enableSkipTrace && (
					<div className="el-alert-modal__skiptrace__body">
						{checkingCard && (
							<div className="el-alert-modal__skiptrace__loading">
								<LinearLoading />
							</div>
						)}

						{!checkingCard && hasCard === false && (
							<div className="el-alert-modal__skiptrace__cc-required">
								<div className="el-alert-modal__skiptrace__cc-required__title">
									<CreditCardIcon size={20} style={{ marginRight: 6 }} />
									Payment method required
								</div>
								<div className="el-alert-modal__skiptrace__cc-required__body">
									A credit card is needed so we can purchase Premium Credits as needed.
									You're only charged when contact info is found for an owner.
								</div>
								<Button
									variant="contained"
									size="small"
									onClick={this._createSetupIntent}
									style={{
										background: "#780F9E",
										color: "#fff",
										textTransform: "none",
										fontWeight: 600
									}}
								>
									<CreditCardIcon size={14} style={{ marginRight: 6 }} />
									Add Card
								</Button>
							</div>
						)}

						{!checkingCard && hasCard === true && (
							<div className="el-alert-modal__skiptrace__autorecharge">
								<AutoRechargeSettings
									compact={false}
									onSave={() => {}}
									autoEnable={!ar.enabled}
								/>
							</div>
						)}
					</div>
				)}
			</div>
		);
	}

	render() {
		const { open, onClose } = this.props;
		const { name, offMarketTypes, submitting, error, success } = this.state;
		const chips = this._filterSummary();
		const className = "modal-dialog--primary modal-dialog--header el-alert-modal " + _getThemeClass();

		return (
			<React.Fragment>
			<Modal
				isOpen={open}
				toggle={onClose}
				className={className}
				scrollable
				style={{ minWidth: 800 }}
			>
				<div
					className="modal__header"
					style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
				>
					<h4 className="bold-text modal__title" style={{ margin: 0 }}>
						<BellPlusIcon size={22} style={{ marginRight: 8, marginTop: -2 }} />
						{this._isEditMode() ? "Edit Alert" : "Save Search & Email Me Daily"}
					</h4>
					<button
						className="lnr lnr-cross modal__close-btn"
						style={{ position: "static" }}
						onClick={onClose}
					/>
				</div>

				<div className="modal__body" style={{ padding: 0, textAlign: "left" }}>
					<div className="el-alert-modal__callout">
						<div className="el-alert-modal__callout__title">How this alert works</div>
						<ul className="el-alert-modal__callout__list">
							<li>Runs daily at <strong>7am ET</strong> against your saved filters below.</li>
							<li>You only get listings that <strong>newly went off-market since your last email</strong> — no repeats.</li>
							<li>If there's nothing new that day, no email is sent.</li>
							<li>Pause, edit, or delete anytime from <strong>My Alerts</strong>.</li>
						</ul>
					</div>

					<div className="el-alert-modal__grid">
						<div className="el-alert-modal__col">
							<label>Alert name</label>
							<input
								type="text"
								value={name}
								onChange={e => this.setState({ name: e.target.value })}
								placeholder="My alert"
								maxLength={120}
							/>

							<label>Max results per email</label>
							<input
								type="number"
								min={1}
								max={100}
								value={this.state.maxResultsInput}
								onChange={e => {
									// Always reflect the raw string so the field can be
									// empty/partial while typing. Only commit a numeric
									// value when it parses; on blur we clamp.
									const v = e.target.value;
									const parsed = parseInt(v, 10);
									this.setState({
										maxResultsInput: v,
										maxResults: Number.isNaN(parsed) ? this.state.maxResults : parsed
									});
								}}
								onBlur={e => {
									const parsed = parseInt(e.target.value, 10);
									let n = Number.isNaN(parsed) ? 10 : parsed;
									if (n < 1) n = 1;
									if (n > 100) n = 100;
									this.setState({ maxResults: n, maxResultsInput: String(n) });
								}}
							/>

							<label>Filters being saved</label>
							<div className="el-alert-modal__filter-summary">
								{chips.length === 0 ? (
									<span className="el-alert-modal__filter-summary__empty">
										No filters set — you'll match all off-market listings citywide.
									</span>
								) : (
									chips.map(c => (
										<span key={c.label} className="chip">
											<span className="chip__label">{c.label}:</span>{' '}
											<span className="chip__value">{c.value}</span>
										</span>
									))
								)}
							</div>
						</div>

						<div className="el-alert-modal__col">
							<label>Off-market types</label>
							<div className="el-alert-modal__types">
								{OFF_MARKET_TYPES.map(t => {
									const checked = offMarketTypes.includes(t.key);
									return (
										<label
											key={t.key}
											className={
												"el-alert-modal__type" +
												(checked ? " el-alert-modal__type--checked" : "")
											}
										>
											<input
												type="checkbox"
												checked={checked}
												onChange={() => this._toggleType(t.key)}
											/>
											<div>
												<div className="el-alert-modal__type__label">{t.label}</div>
												<div className="el-alert-modal__type__hint">{t.hint}</div>
											</div>
										</label>
									);
								})}
							</div>
						</div>
					</div>

					{this._renderSkipTraceSection()}

					{submitting && <LinearLoading />}

					<div className="el-alert-modal__footer">
						<div className="el-alert-modal__footer-msg">
							{error && <span className="el-alert-modal__error">{error}</span>}
							{success && (
								<span className="el-alert-modal__success">
									{this._isEditMode()
										? "Changes saved."
										: "Saved. First email arrives tomorrow at 7am ET."}
								</span>
							)}
						</div>
						<div>
							<Button
								onClick={onClose}
								disabled={submitting}
								style={{ marginRight: 8, textTransform: "none" }}
							>
								{success ? "Close" : "Cancel"}
							</Button>
							{!success && (
								<Button
									onClick={this._handleSubmit}
									disabled={submitting}
									variant="contained"
									style={{
										textTransform: "none",
										background: "#780F9E",
										color: "#fff"
									}}
								>
									{this._isEditMode() ? "Save Changes" : "Save Alert"}
								</Button>
							)}
						</div>
					</div>
				</div>
			</Modal>

			{this.state.showPaymentModal && this.state.setupClientSecret && (
				<StripeCardSetupModal
					isOpen={this.state.showPaymentModal}
					clientSecret={this.state.setupClientSecret}
					user={{
						name: ((this.props.user && this.props.user.firstName) || "") + " " + ((this.props.user && this.props.user.lastName) || ""),
						email: this.props.user && this.props.user.email
					}}
					onClose={() => this.setState({ showPaymentModal: false })}
					onComplete={() => {
						this.setState({ showPaymentModal: false });
						this._checkCard();
					}}
				/>
			)}
		</React.Fragment>
		);
	}
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({ setUserProfile: setUserProfile }, dispatch);
}

export default connect(null, mapDispatchToProps)(SaveAlertModal);
